import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FiX,
  FiFileText,
  FiDollarSign,
  FiAlertTriangle,
  FiFile,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiXCircle,
  FiCalendar,
  FiUser,
  FiInfo,
  FiPrinter,
  FiZap
} from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import { useImpresion } from '../../contexts/ContextoImpresion';
import SpinnerCargando from '../util/SpinnerCargando';
import ModalPDFBase64 from '../util/ModalPDFBase64';
import ModalPagoPOS from '../venta/ModalPagoPOS';
import ModalCrearNotaFactura from '../util/modals/ModalCrearNotaFactura';
import PlantillaModal from '../util/PlantillaModal';
import { roundTo2 } from '../../miscelaneo/monetario';

// =====================================================
// Helper: format currency
// =====================================================
function fmt(valor) {
  return roundTo2(parseFloat(valor) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function resolverNombreClienteFactura(factura = {}) {
  const razonSocial = String(factura.cliente_razon_social || factura.razon_social || '').trim();
  if (razonSocial) return razonSocial;

  const nombre = String(factura.cliente_nombre || factura.nombre_cliente || '').trim();
  return nombre || 'CONSUMIDOR FINAL';
}

// =====================================================
// Helper: payment status badge
// =====================================================
function BadgeEstadoPago({ estado }) {
  const config = {
    pagada:    { bg: 'bg-green-100', text: 'text-green-800', icon: FiCheckCircle, label: 'Pagada' },
    parcial:   { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FiClock, label: 'Parcial' },
    pendiente: { bg: 'bg-red-100', text: 'text-red-800', icon: FiAlertTriangle, label: 'Pendiente' }
  };
  const c = config[estado] || config.pendiente;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${c.bg} ${c.text}`}>
      <Icon size={14} /> {c.label}
    </span>
  );
}

// =====================================================
// TAB: General (invoice header + line items)
// =====================================================
function TabGeneral({ factura }) {
  if (!factura) return null;
  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <InfoCard label="NCF" value={factura.ncf || 'Sin NCF'} />
        <InfoCard label="Cliente" value={resolverNombreClienteFactura(factura)} />
        {(!!factura.ncf || factura.mostrar_rnc_factura_demo === true) && (
          <InfoCard label="RNC" value={factura.cliente_rnc || factura.rnc_cliente || factura.rnc || '—'} />
        )}
        <InfoCard label="Cajero" value={factura.nombre_usuario || '—'} />
        <InfoCard label="Fecha de Creación" value={(factura.fecha_hora_creacion || factura.fecha_hora) ? new Date(factura.fecha_hora_creacion || factura.fecha_hora).toLocaleDateString('es-DO') : '—'} />
        <InfoCard label="Fecha de Vencimiento" value={factura.fecha_vencimiento ? new Date(factura.fecha_vencimiento).toLocaleDateString('es-DO') : '—'} />
        <InfoCard label="Condición de Venta" value={factura.condicion_venta || '—'} />
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <TotalCard label="Subtotal" value={fmt(factura.monto)} color="blue" />
        <TotalCard label="ITBIS" value={fmt(factura.impuesto_total)} color="purple" />
        <TotalCard label="Total" value={fmt(factura.monto_total)} color="green" />
        <TotalCard label="Balance" value={fmt(factura.balance)} color={parseFloat(factura.balance) > 0 ? 'red' : 'green'} />
      </div>

      {/* Line items table */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Detalle de Productos</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Producto</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Cant.</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Precio</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">ITBIS</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(factura.detalles_factura || []).map((d, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-800">{d.descripcion_producto || d.descripcion || d.producto_nombre || '—'}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{d.cantidad}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmt(d.precio)}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmt(d.itbis)}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">{fmt(d.total)}</td>
                </tr>
              ))}
              {(!factura.detalles_factura || factura.detalles_factura.length === 0) && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Sin productos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Comment */}
      {factura.comentario && (
        <div className="bg-gray-50 p-3 rounded-lg">
          <span className="text-xs font-medium text-gray-500">Comentario:</span>
          <p className="text-sm text-gray-700 mt-1">{factura.comentario}</p>
        </div>
      )}
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">{value}</p>
    </div>
  );
}

function TotalCard({ label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200'
  };
  return (
    <div className={`rounded-lg p-3 border ${colors[color] || colors.blue}`}>
      <span className="text-xs font-medium opacity-75">{label}</span>
      <p className="text-lg font-bold mt-0.5">${value}</p>
    </div>
  );
}

// =====================================================
// SUB-COMPONENT: Modal de Anulación de Pago (CxC)
// =====================================================
function ModalAnulacionFactura({ abierto, onCerrar, pago, onAnulado }) {
  const [razones, setRazones] = useState([]);
  const [formData, setFormData] = useState({ id_razon_anulacion: '', comentario: '' });
  const [anulando, setAnulando] = useState(false);

  useEffect(() => {
    if (abierto) {
      setFormData({ id_razon_anulacion: '', comentario: '' });
      instanciaAxios.get('/pago-factura/razones-anulacion')
        .then(res => setRazones(res.data))
        .catch(() => {
          setRazones([
            { id_razon_anulacion: 1, descripcion: 'Error de digitación' },
            { id_razon_anulacion: 2, descripcion: 'Pago duplicado' },
            { id_razon_anulacion: 3, descripcion: 'Monto incorrecto' },
            { id_razon_anulacion: 4, descripcion: 'Método de pago incorrecto' },
            { id_razon_anulacion: 5, descripcion: 'Otro' }
          ]);
        });
    }
  }, [abierto]);

  const handleAnular = async () => {
    if (!formData.id_razon_anulacion) {
      toast.error('Seleccione una razón de anulación');
      return;
    }
    setAnulando(true);
    try {
      await instanciaAxios.put(`/pago-factura/${pago.id_pago_factura}/anular`, formData);
      toast.success('Pago anulado correctamente');
      onAnulado();
      onCerrar();
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error(error.response?.data?.mensaje || 'Error al anular pago');
    } finally {
      setAnulando(false);
    }
  };

  if (!pago) return null;

  return (
    <PlantillaModal mostrar={abierto} onClose={onCerrar} titulo="Anular Pago">
      <div className="space-y-5">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="text-amber-500 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-amber-800">¿Está seguro de anular este pago?</p>
              <p className="text-sm text-amber-700 mt-1">
                Monto: <span className="font-semibold">RD$ {fmt(pago.monto)}</span>
              </p>
              <p className="text-sm text-amber-700">Método: {pago.metodo_pago_nombre}</p>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Razón de Anulación *</label>
          <select
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            value={formData.id_razon_anulacion}
            onChange={(e) => setFormData(prev => ({ ...prev, id_razon_anulacion: e.target.value }))}
          >
            <option value="">Seleccione una razón...</option>
            {razones.map(r => (
              <option key={r.id_razon_anulacion} value={r.id_razon_anulacion}>{r.descripcion}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Comentario (opcional)</label>
          <textarea
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            rows={3}
            placeholder="Detalles adicionales..."
            value={formData.comentario}
            onChange={(e) => setFormData(prev => ({ ...prev, comentario: e.target.value }))}
          />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onCerrar} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" disabled={anulando}>
            Cancelar
          </button>
          <button onClick={handleAnular} disabled={anulando} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50">
            {anulando ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Anulando...</>
            ) : (
              <><FiXCircle size={18} />Confirmar Anulación</>
            )}
          </button>
        </div>
      </div>
    </PlantillaModal>
  );
}

// =====================================================
// SUB-COMPONENT: Modal de Conversión Factura Demo -> Fiscal
// =====================================================
function ModalConvertirFacturaFiscal({ abierto, onCerrar, factura, onConvertida }) {
  const [tipoComprobante, setTipoComprobante] = useState('2');
  const [convirtiendo, setConvirtiendo] = useState(false);
  const [confirmacionSerieB, setConfirmacionSerieB] = useState(null); // mensaje o null

  useEffect(() => {
    if (abierto) {
      setTipoComprobante('2');
      setConfirmacionSerieB(null);
    }
  }, [abierto]);

  if (!factura) return null;

  const rnc = factura.cliente_rnc || factura.rnc_cliente || factura.rnc || '';
  const rncValido = /^[0-9]{9}$|^[0-9]{11}$/.test(String(rnc).replace(/[-\s]/g, ''));

  const enviar = async (usarSerieB = false) => {
    setConvirtiendo(true);
    try {
      const resp = await instanciaAxios.post(`/factura/${factura.id_factura}/convertir-fiscal`, {
        tipo_comprobante: tipoComprobante,
        usarSerieB
      });

      if (resp.data?.pendienteEnvio) {
        const dgii = resp.data?.data?.dgii;
        if (dgii?.es_error_certificado) {
          toast.error(
            `⚠️ CERTIFICADO DIGITAL RECHAZADO POR DGII\n\nLa factura se convirtió a fiscal, pero DGII no la pudo recibir: ${dgii?.mensaje || 'certificado vencido o revocado'}.\n\nTODAS las facturas electrónicas quedarán pendientes hasta que actualice el certificado. Hágalo cuanto antes y reenvíe desde Pendientes DGII.`,
            { autoClose: false, closeOnClick: false }
          );
        } else {
          toast.warning(
            `${resp.data?.mensaje || 'Factura convertida a fiscal.'}${dgii?.mensaje ? ` (${dgii.mensaje})` : ''}`,
            { autoClose: 12000 }
          );
        }
      } else {
        toast.success(resp.data?.mensaje || 'Factura convertida a fiscal exitosamente');
      }

      onConvertida();
      onCerrar();
    } catch (error) {
      if (esErrorPermiso(error)) return;

      const codigo = error.response?.data?.codigo;
      const puedeUsarB = error.response?.data?.puedeUsarB;

      if (error.response?.status === 409 && codigo === 'SIN_SECUENCIA_E' && puedeUsarB) {
        setConfirmacionSerieB(error.response.data.mensaje);
        return;
      }

      toast.error(error.response?.data?.mensaje || 'Error al convertir la factura a fiscal');
    } finally {
      setConvirtiendo(false);
    }
  };

  return (
    <PlantillaModal mostrar={abierto} onClose={onCerrar} titulo="Convertir a Factura Fiscal">
      <div className="space-y-5">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FiZap className="text-blue-500 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-blue-800">Esta factura no tiene NCF (es una factura demo)</p>
              <p className="text-sm text-blue-700 mt-1">
                Al convertirla se le asignará un comprobante fiscal electrónico y se enviará a la DGII.
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
        </div>

        {confirmacionSerieB ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
            <p className="text-sm text-amber-800">{confirmacionSerieB}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmacionSerieB(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={convirtiendo}
              >
                Cancelar
              </button>
              <button
                onClick={() => enviar(true)}
                disabled={convirtiendo}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {convirtiendo ? 'Convirtiendo...' : 'Usar comprobante físico (B)'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Comprobante *</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="tipo_comprobante"
                    checked={tipoComprobante === '2'}
                    onChange={() => setTipoComprobante('2')}
                  />
                  <span className="text-sm text-gray-800">Factura de Consumo (E32)</span>
                </label>
                <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="tipo_comprobante"
                    checked={tipoComprobante === '1'}
                    onChange={() => setTipoComprobante('1')}
                  />
                  <span className="text-sm text-gray-800">Crédito Fiscal (E31)</span>
                </label>
              </div>
              {tipoComprobante === '1' && !rncValido && (
                <p className="text-xs text-red-600 mt-2">
                  El cliente no tiene un RNC/Cédula válido registrado. E31 requiere RNC válido; use E32 o actualice el RNC del cliente.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={onCerrar} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" disabled={convirtiendo}>
                Cancelar
              </button>
              <button
                onClick={() => enviar(false)}
                disabled={convirtiendo || (tipoComprobante === '1' && !rncValido)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {convirtiendo ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Convirtiendo...</>
                ) : (
                  <><FiZap size={18} />Convertir y Enviar a DGII</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </PlantillaModal>
  );
}

// =====================================================
// TAB: Pagos (payments list + register new + print receipt + annul)
// =====================================================
function TabPagos({ factura, onPagoRegistrado, formatoMoneda, tienePermisoAnular, onAnularPago, imprimirDocumento }) {
  const [reciboPdf, setReciboPdf] = useState(null);
  const [cargandoRecibo, setCargandoRecibo] = useState(null);

  const pagos = factura?.pagos_factura || [];
  const pagosActivos = pagos.filter(p => !p.anulado);
  const totalPagado = roundTo2(pagosActivos.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0));
  const balance = parseFloat(factura?.balance) || 0;

  const handleImprimirRecibo = async (idPago) => {
    setCargandoRecibo(idPago);
    try {
      const resp = await instanciaAxios.get(`/pago-factura/${idPago}/recibo`);
      if (resp.data?.pdf_base64) {
        if (imprimirDocumento) {
          const metodo = await imprimirDocumento({ tipoDocumento: 'recibo_pago', pdfBase64: resp.data.pdf_base64 });
          if (!metodo) {
            setReciboPdf(resp.data.pdf_base64);
          }
        } else {
          setReciboPdf(resp.data.pdf_base64);
        }
      } else {
        toast.error('No se pudo obtener el recibo');
      }
    } catch (error) {
      console.error('Error al generar recibo:', error);
      toast.error('Error al generar el recibo de pago');
    } finally {
      setCargandoRecibo(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
          <span className="text-xs font-medium text-green-600">Total Pagado</span>
          <p className="text-lg font-bold text-green-700">${fmt(totalPagado)}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3 border border-red-200">
          <span className="text-xs font-medium text-red-600">Balance Pendiente</span>
          <p className="text-lg font-bold text-red-700">${fmt(balance)}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
          <span className="text-xs font-medium text-blue-600">Pagos Registrados</span>
          <p className="text-lg font-bold text-blue-700">{pagosActivos.length}</p>
        </div>
      </div>

      {/* Payment table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Fecha</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Método</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">Monto</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Detalles</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Usuario</th>
              <th className="text-center px-3 py-2 font-medium text-gray-600">Estado</th>
              <th className="text-center px-3 py-2 font-medium text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pagos.map((p, i) => (
              <tr key={i} className={`hover:bg-gray-50 ${p.anulado ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                  {p.fecha_hora ? new Date(p.fecha_hora).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  <div className="flex items-center gap-1">
                    <FiCreditCard size={14} className="text-gray-400" />
                    {p.metodo_pago_nombre || '—'}
                  </div>
                </td>
                <td className={`px-3 py-2 text-right font-medium ${p.anulado ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                  ${fmt(p.monto)}
                </td>
                <td className="px-3 py-2 text-gray-600 text-xs">
                  {p.ultimos_4_digitos && <span className="mr-2">****{p.ultimos_4_digitos}</span>}
                  {p.banco_origen_nombre && <span className="mr-2">{p.banco_origen_nombre}</span>}
                  {p.numero_cheque && <span className="mr-2">Cheque #{p.numero_cheque}</span>}
                  {p.pasarela_nombre && <span className="mr-2">{p.pasarela_nombre}</span>}
                  {p.referencia && <span className="text-gray-400">Ref: {p.referencia}</span>}
                  {!p.ultimos_4_digitos && !p.banco_origen_nombre && !p.numero_cheque && !p.referencia && !p.pasarela_nombre && '—'}
                </td>
                <td className="px-3 py-2 text-gray-600 text-xs">{p.usuario_nombre || '—'}</td>
                <td className="px-3 py-2 text-center">
                  {p.anulado ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      <FiXCircle size={12} /> Anulado
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <FiCheckCircle size={12} /> Activo
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {!p.anulado && (
                      <button
                        onClick={() => handleImprimirRecibo(p.id_pago_factura)}
                        disabled={cargandoRecibo === p.id_pago_factura}
                        className="inline-flex items-center justify-center p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                        title="Imprimir recibo de pago"
                      >
                        {cargandoRecibo === p.id_pago_factura ? (
                          <span className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                        ) : (
                          <FiPrinter size={16} />
                        )}
                      </button>
                    )}
                    {!p.anulado && tienePermisoAnular && (
                      <button
                        onClick={() => onAnularPago(p)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Anular pago"
                      >
                        <FiXCircle size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {pagos.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No hay pagos registrados</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Recibo PDF Modal */}
      <ModalPDFBase64
        mostrar={!!reciboPdf}
        onClose={() => setReciboPdf(null)}
        pdfBase64Directo={reciboPdf}
        tituloDocumento="Recibo de Pago"
        tipoDocumento="recibo_pago"
      />
    </div>
  );
}

// =====================================================
// TAB: Notas (credit & debit notes)
// =====================================================
function TabNotas({ factura, onCrearNota, pagosActivos = [], onAnularParaCrearNota }) {
  const notas = [
    ...(factura?.notas_credito || []),
    ...(factura?.notas_debito || [])
  ].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

  const tieneNcf = !!factura?.ncf;
  const balance = parseFloat(factura?.balance) || 0;
  const deshabilitarNC = !tieneNcf || balance <= 0;
  const deshabilitarND = !tieneNcf;

  const tituloNC = !tieneNcf
    ? 'La factura no tiene NCF'
    : balance <= 0
      ? 'La factura no tiene balance disponible'
      : 'Crear Nota de Crédito';
  const tituloND = !tieneNcf ? 'La factura no tiene NCF' : 'Crear Nota de Débito';

  const mostrarAyudaAnular = tieneNcf && balance <= 0 && pagosActivos.length > 0 && !!onAnularParaCrearNota;

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-3">
        <button
          onClick={() => onCrearNota('credito')}
          disabled={deshabilitarNC}
          title={tituloNC}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Nueva Nota de Crédito
        </button>
        <button
          onClick={() => onCrearNota('debito')}
          disabled={deshabilitarND}
          title={tituloND}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Nueva Nota de Débito
        </button>
      </div>
      {mostrarAyudaAnular && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
          <p className="text-sm text-amber-800 font-medium">
            Esta factura ya no tiene balance disponible (fue pagada por completo). Para crear
            la Nota de Crédito, primero anule uno de los pagos:
          </p>
          <div className="flex flex-wrap gap-2">
            {pagosActivos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onAnularParaCrearNota(p)}
                className="px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
              >
                Anular {p.metodo_pago_nombre || 'pago'} de ${fmt(p.monto)}
                {p.fecha_hora ? ` (${new Date(p.fecha_hora).toLocaleDateString('es-DO')})` : ''}
              </button>
            ))}
          </div>
        </div>
      )}
      {notas.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FiInfo size={32} className="mx-auto mb-2 opacity-50" />
          <p>No hay notas de crédito o débito asociadas</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Tipo</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">NCF</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">NCF Modificado</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Monto</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Fecha</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Razón</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Estado DGII</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {notas.map((n, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      n.tipo_nota === 'credito' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {n.tipo_nota === 'credito' ? 'NC' : 'ND'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-800 font-mono text-xs">{n.ncf || '—'}</td>
                  <td className="px-4 py-2 text-gray-600 font-mono text-xs">{n.ncf_modificado || '—'}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">${fmt(n.monto_total)}</td>
                  <td className="px-4 py-2 text-gray-700">
                    {n.fecha ? new Date(n.fecha).toLocaleDateString('es-DO') : '—'}
                  </td>
                  <td className="px-4 py-2 text-gray-600 text-xs">{n.razon_modificacion || '—'}</td>
                  <td className="px-4 py-2 text-gray-600 text-xs">{n.dgii_estado || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =====================================================
// MAIN: ModalDetalleFactura
// =====================================================
export default function ModalDetalleFactura({ abierto, onCerrar, idFactura, onFacturaActualizada }) {
  const { tienePermiso } = useContextoSesion();
  const { imprimirDocumento } = useImpresion();
  const [factura, setFactura] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [tabActivo, setTabActivo] = useState('general');
  const [mostrarPDF, setMostrarPDF] = useState(false);
  const [mostrarModalPago, setMostrarModalPago] = useState(false);
  const [pagoAnular, setPagoAnular] = useState(null);
  const [modalAnulacionAbierto, setModalAnulacionAbierto] = useState(false);
  const [mostrarModalNota, setMostrarModalNota] = useState(false);
  const [tipoNotaCrear, setTipoNotaCrear] = useState('credito');
  const [mostrarModalConvertir, setMostrarModalConvertir] = useState(false);
  // Cuando el usuario elige "anular pago para crear NC" desde la tab Notas,
  // este flag hace que, al confirmar la anulación, se abra automáticamente
  // el modal de Nota de Crédito en vez de solo recargar los datos.
  const [flujoNotaTrasAnular, setFlujoNotaTrasAnular] = useState(false);

  // Load invoice data
  const cargarFactura = useCallback(async () => {
    if (!idFactura) return;
    setCargando(true);
    try {
      const resp = await instanciaAxios.get(`/factura/${idFactura}`);
      setFactura(resp.data);
    } catch (error) {
      console.error('Error al cargar factura:', error);
      toast.error('Error al cargar los detalles de la factura');
    } finally {
      setCargando(false);
    }
  }, [idFactura]);

  useEffect(() => {
    if (abierto && idFactura) {
      setTabActivo('general');
      setFactura(null);
      cargarFactura();
    }
  }, [abierto, idFactura, cargarFactura]);

  const handlePagoRegistrado = () => {
    setMostrarModalPago(false);
    cargarFactura(); // Reload invoice data after payment
    if (onFacturaActualizada) onFacturaActualizada();
  };

  if (!abierto) return null;

  const tabs = [
    { id: 'general', label: 'General', icon: FiFileText },
    { id: 'pagos', label: 'Pagos', icon: FiDollarSign, badge: factura?.pagos_factura?.filter(p => !p.anulado).length },
    { id: 'notas', label: 'Notas', icon: FiAlertTriangle, badge: (factura?.notas_credito?.length || 0) + (factura?.notas_debito?.length || 0) },
    { id: 'pdf', label: 'Imprimir', icon: FiPrinter },
    { id: 'ver_pdf', label: 'Ver / Descargar PDF', icon: FiFile }
  ];

  const balance = parseFloat(factura?.balance) || 0;
  const tituloFactura = cargando
    ? 'Cargando...'
    : factura?.ncf
      ? factura.ncf
      : factura
        ? 'Factura sin NCF'
        : 'Detalle de factura';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onCerrar} />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-8 lg:inset-12 z-50 flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                {tituloFactura}
              </h2>
              <div className="flex items-center gap-3 mt-1">
                {factura && <BadgeEstadoPago estado={factura.estado_pago} />}
                {factura?.es_electronica && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                    e-CF
                  </span>
                )}
                {factura?.dgii_estado && (
                  <span className="text-xs text-gray-500">DGII: {factura.dgii_estado}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {factura && !factura.ncf && tienePermiso('venta_crear_factura') && (
              <button
                onClick={() => setMostrarModalConvertir(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                title="Asignar NCF y enviar esta factura a la DGII"
              >
                <FiZap size={16} />
                Convertir a Fiscal
              </button>
            )}
            {balance > 0 && factura && (
              <button
                onClick={() => setMostrarModalPago(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <FiDollarSign size={16} />
                Registrar Pago
              </button>
            )}
            <button
              onClick={onCerrar}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white px-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = tabActivo === tab.id;
            return (
              <button
                key={tab.id}
                onClick={async () => {
                  if (tab.id === 'pdf') {
                    try {
                      const metodo = await imprimirDocumento({ tipoDocumento: 'factura', idDocumento: idFactura });
                      console.log('[ConsultarFacturas] Resultado impresion factura:', { idFactura, metodo });
                      if (!metodo) {
                        setMostrarPDF(true);
                      }
                    } catch (error) {
                      console.warn('[ConsultarFacturas] Error en impresion de factura, abriendo fallback PDF:', error?.message);
                      setMostrarPDF(true);
                    }
                  } else if (tab.id === 'ver_pdf') {
                    setMostrarPDF(true);
                  } else {
                    setTabActivo(tab.id);
                  }
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {cargando ? (
            <div className="flex items-center justify-center py-20">
              <SpinnerCargando />
            </div>
          ) : !factura ? (
            <div className="text-center py-20 text-gray-400">
              <FiInfo size={40} className="mx-auto mb-3 opacity-50" />
              <p>No se pudieron cargar los datos de la factura</p>
            </div>
          ) : (
            <>
              {tabActivo === 'general' && <TabGeneral factura={factura} />}
              {tabActivo === 'pagos' && (
                <TabPagos
                  factura={factura}
                  onPagoRegistrado={handlePagoRegistrado}
                  formatoMoneda={fmt}
                  tienePermisoAnular={tienePermiso('venta_anular_pago')}
                  onAnularPago={(pago) => {
                    setPagoAnular(pago);
                    setModalAnulacionAbierto(true);
                  }}
                  imprimirDocumento={imprimirDocumento}
                />
              )}
              {tabActivo === 'notas' && (
                <TabNotas
                  factura={factura}
                  onCrearNota={(tipo) => {
                    setTipoNotaCrear(tipo);
                    setMostrarModalNota(true);
                  }}
                  pagosActivos={(factura?.pagos_factura || []).filter(p => !p.anulado)}
                  onAnularParaCrearNota={tienePermiso('venta_anular_pago') ? (pago) => {
                    setPagoAnular(pago);
                    setFlujoNotaTrasAnular(true);
                    setModalAnulacionAbierto(true);
                  } : undefined}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* PDF Modal */}
      <ModalPDFBase64
        mostrar={mostrarPDF}
        onClose={() => setMostrarPDF(false)}
        idFactura={idFactura}
        ncf={factura?.ncf}
        tituloDocumento="Factura"
        tipoDocumento="factura"
      />

      {/* Payment Modal */}
      {mostrarModalPago && factura && (
        <ModalPagoPOS
          mostrar={mostrarModalPago}
          onClose={() => setMostrarModalPago(false)}
          ticket={{
            totales: {
              total: parseFloat(factura.balance) || 0,
              subtotal: parseFloat(factura.monto) || 0,
              itbis: parseFloat(factura.impuesto_total) || 0
            }
          }}
          onConfirmarPago={handlePagoRegistrado}
          formatoMoneda={(v) => `$${fmt(v)}`}
          modo="cxc"
          facturaExistente={{
            id_factura: factura.id_factura,
            balance: parseFloat(factura.balance) || 0,
            monto_total: parseFloat(factura.monto_total) || 0
          }}
        />
      )}

      {/* Annulment Modal */}
      <ModalAnulacionFactura
        abierto={modalAnulacionAbierto}
        onCerrar={() => {
          setModalAnulacionAbierto(false);
          setPagoAnular(null);
          setFlujoNotaTrasAnular(false);
        }}
        pago={pagoAnular}
        onAnulado={async () => {
          await cargarFactura();
          onFacturaActualizada && onFacturaActualizada();
          if (flujoNotaTrasAnular) {
            setFlujoNotaTrasAnular(false);
            setTipoNotaCrear('credito');
            setMostrarModalNota(true);
          }
        }}
      />

      {/* Modal Convertir a Fiscal */}
      <ModalConvertirFacturaFiscal
        abierto={mostrarModalConvertir}
        onCerrar={() => setMostrarModalConvertir(false)}
        factura={factura}
        onConvertida={async () => {
          await cargarFactura();
          onFacturaActualizada && onFacturaActualizada();
        }}
      />

      {/* Modal Crear NC/ND */}
      <ModalCrearNotaFactura
        mostrar={mostrarModalNota}
        onClose={() => setMostrarModalNota(false)}
        facturaOriginal={factura ? { ...factura, nombre_cliente: resolverNombreClienteFactura(factura) } : null}
        tipoNota={tipoNotaCrear}
        onExito={() => {
          setMostrarModalNota(false);
          cargarFactura();
          onFacturaActualizada && onFacturaActualizada();
        }}
      />
    </>
  );
}

